/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * DynamicCreateEditForm Component
 * --------------------------------
 * A fully dynamic form generator used for creating or editing records of any table.
 * The form structure is built from table metadata returned by the backend.
 *
 * - Renders inputs for data types: string, integer, float, decimal, text, date, datetime, boolean.
 * - Relationship fields (fk, one-to-one, one-to-many, self) open a selector dialog.
 *   Selected record returns `{ record, recordIdentifier }`. We store:
 *     - [field_name] => selected record id (sent to backend)
 *     - [field_name]_display => human readable label (UI only)
 * - Before submit we remove all `*_display` keys and convert empty values for non-string types to `null`.
 *
 * Notes:
 * - `allowed_characters` is optional on field metadata. If present, we will show a readable hint.
 * - This file preserves the existing form behavior; only UI hints and small helpers were added.
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import AlertDialog from "../components/common/AlertDialog";
import envConfig from '../config/envConfig';
import APIHelper from '../api/APIHelper';
import GradientBar from '../components/common/GradientBar';
import CalendarInput from '../components/common/CalenderInput';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ProgressBarDialog from '../components/common/ProgressBar';
import Button from '../components/Button';
import DynamicTableRecordShowDialog from '../components/DynamicTableRecordShowDailog';
import "../styles/DynamicCreateEditForm.css";
import { convertUTCStringToLocalStandard, convertLocalStringToUTCISO } from "../utils/utils";
import { retrieve } from '../utils/storage_helper';
import { AUTO_MANAGED_FIELDS } from '../utils/constants';
import FileUploadInput, { uploadPendingFile, deleteUploadedFile } from '../components/common/FileUploadInput';
import { useQuery } from '@tanstack/react-query';
import { useBreadcrumbNames } from '../context/BreadCrumbNameContext';
import { useQueryClient } from "@tanstack/react-query";

import {
  isFieldVisible,
  isFieldReadOnly,
  getInitialFieldValue,
  getFieldPlaceholder,
  getFieldHintForRole,
  getFieldOptionsForRole
} from "../utils/uiRulesHelper";


const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const applicationId = envConfig.applicationId;
const organizationId = envConfig.organizationId;

export default function DynamicCreateEditForm({ initialData = null, mode = 'create', onCancel = null }) {
  const { theme } = useTheme();
  const { showAlertDialog } = useAlertDialog();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tableName, recordId } = useParams(); //
  const location = useLocation();
  const { setTableDisplayNames, tableDisplayNames } = useBreadcrumbNames();

  const [recordState] = useState(() => location.state?.record || null);
  const propMode = typeof mode === 'string' ? mode : 'create';
  const effectiveMode = recordId ? "update" : (location.state?.mode || propMode);

  const [fetchedRecord, setFetchedRecord] = useState(null);

  const [error, setError] = useState(null);

  const [fields, setFields] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Map of fieldName → File object the user picked but hasn't submitted yet.
  // These get uploaded inside handleSubmit, AFTER local validation passes,
  // so a customer can never create an orphan file by abandoning the form.
  const [pendingUploads, setPendingUploads] = useState({});

  // Snapshot of the file URLs that were present on the record when we opened
  // the form (update mode only). Lets us detect which files were replaced or
  // cleared so we can ask Flask to delete them after a successful save.
  const [originalFileUrls, setOriginalFileUrls] = useState({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTableName, setDialogTableName] = useState(null);
  const [dialogFieldName, setDialogFieldName] = useState(null);

  const [tableDisplayName, setTableDisplayName] = useState('');
  const [currentRoleName, setCurrentRoleName] = useState(null);
  const [uiRules, setUiRules] = useState({});

  const tableDetailsQueryKey = ['tableDetails', tableName, applicationId, organizationId];
  const fromPage = location.state?.fromPage || 1;

  const {
    data: detailsData,
    error: detailsError,
    isLoading: detailsLoading,
    isFetching: detailsFetching,
  } = useQuery({
    queryKey: tableDetailsQueryKey,
    queryFn: async ({ signal }) => {
      if (!tableName) return null;

      const payload = {
        application_id: applicationId,
        organization_id: organizationId,
        table_name: tableName,
      };

      const resp = await apiHelper.apiCall('/arcee/application/table/details/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal,
      });


      if (!resp.ok) {
        let j = {};
        try { j = await resp.json(); } catch (e) { }
        throw new Error(j?.error || `Failed to load table details (${resp.status})`);
      }

      return await resp.json();
    },
    enabled: !!tableName,
    staleTime: envConfig.staleTimeMs,
    retry: 1,
  });

  const readableType = (dt) => {
    if (!dt) return 'UNKNOWN';
    const t = dt.toLowerCase();
    if (t === 'string') return 'STRING';
    if (t === 'text') return 'TEXT';
    if (t === 'integer') return 'INTEGER';
    if (t === 'float') return 'FLOAT';
    if (t === 'decimal') return 'DECIMAL';
    if (t === 'date') return 'DATE';
    if (t === 'datetime') return 'DATETIME';
    if (t === 'boolean') return 'BOOLEAN';
    if (t === 'fk') return 'Foreign Key (references another table)';
    if (t === 'one-to-one') return 'One-to-One Relationship';
    if (t === 'one-to-many') return 'One-to-Many Relationship';
    if (t === 'self') return 'Self-Reference (links within the same table)';
    return t.toUpperCase();
  };


  /**
  * Fetches table schema, loads record data, and initializes form.
  * Runs once whenever tableName changes.
  * @async
  * @returns {Promise<void>}
  */
  async function fetchFields() {
    if (!tableName) return;

    if (!detailsData) {
      return;
    }

    setLoading(true);
    try {
      const resp = detailsData;

      if (resp.role && resp.role.name) {
        setCurrentRoleName(resp.role.name);
      } else {
        setCurrentRoleName(null);
      }

      setUiRules(resp.ui_rules || {});

      const schemaFields = (resp.fields || []).filter(
        f => !AUTO_MANAGED_FIELDS.includes(f.name)
      );

      setFields(schemaFields);

      const displayName = resp.display_name || tableName;

      setTableDisplayName(displayName);

      setTableDisplayNames(prev => ({
        ...prev,
        [tableName]: displayName,
      }));

      const finalRecord = recordState || initialData || null;

      const initData = {};

      resp.fields.forEach(f => {
        let value = finalRecord ? finalRecord[f.name] : undefined;
        let valueFromRecord = finalRecord ? finalRecord[f.name] : undefined;

        if (effectiveMode === "update" && finalRecord && f.related_table_name) {
          if (value && typeof value === "object" && value.data) {
            const identifier = value.record_identifier || "id";
            const displayValue = value.data[identifier] || value.data.id || "";
            initData[f.name] = value.data.id;
            initData[`${f.name}_display`] = displayValue;
          } else {
            initData[f.name] = null;
            initData[`${f.name}_display`] = "";
          }
          return;
        }

        if (effectiveMode === "update" && finalRecord) {
          if (f.data_type === "datetime" && value) {
            value = convertUTCStringToLocalStandard(value);
          }
          if (f.data_type === "json" && value && typeof value === "object") {
            initData[f.name] = JSON.stringify(value, null, 2);
            return;
          }
          if (f.data_type === "geo_point") {
            initData[f.name] = value || { lat: "", lng: "" };
            return;
          }
          initData[f.name] =
            f.data_type === "boolean" ? !!value : value;

          return;
        }
        if (f.data_type === "geo_point") {
          initData[f.name] = { lat: "", lng: "" };
          return;
        }
        initData[f.name] = f.data_type === "boolean" ? null : "";
        initData[f.name] = getInitialFieldValue(f, valueFromRecord, effectiveMode);
      });

      setFormData(initData);

      // Snapshot any existing image/file URLs so handleSubmit can later
      // detect which of them were replaced or cleared and tell Flask to
      // remove the underlying blob (avoids stale files on disk).
      const snapshot = {};
      (resp.fields || []).forEach((f) => {
        const dt = (f.data_type || "").toLowerCase();
        if (dt === "image" || dt === "file") {
          const v = initData[f.name];
          if (v && typeof v === "string") snapshot[f.name] = v;
        }
      });
      setOriginalFileUrls(snapshot);
      setPendingUploads({});

    } catch (err) {
      setError(err.message || "Failed to load form fields.");
    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    if (!tableName) return;
    fetchFields();
  }, [tableName, initialData, detailsData]);

  useEffect(() => {
    if (detailsError) {
      setError(detailsError?.message || String(detailsError));
    }
  }, [detailsError]);

  /**
   * Opens relationship selector dialog.
   * @param {string} fieldName - The field to update.
   * @param {string} relatedTableName - Table to fetch records from.
   */
  function openDialog(fieldName, relatedTableName) {
    setDialogFieldName(fieldName);
    setDialogTableName(relatedTableName);
    setDialogOpen(true);
  }

  /**
   * Handles selection inside relationship dialog.
   * Updates form with record ID and UI display value.
   * @param {Object} selectedData - Object containing record and recordIdentifier.
   */
  function handleDialogSelect(selectedData) {
    if (!selectedData || !selectedData.record) {
      setDialogOpen(false);
      return;
    }
    const actualRecord = selectedData.record;
    const identifier = selectedData.recordIdentifier;
    const displayValue = identifier ? actualRecord[identifier] : actualRecord.id;

    setFormData((prev) => ({
      ...prev,
      [dialogFieldName]: actualRecord.id,
      [`${dialogFieldName}_display`]: displayValue,
    }));
    setDialogOpen(false);
  }

  /**
   * Handles image field changes coming from FileUploadInput.
   * This function updates the specific field in formData.
   *
   * @param {string} url - The uploaded image URL or empty string when cleared.
   * @param {string} name - The field name (column name).
   */
  const handleImageChange = (url, name) => {
    setFormData((prev) => ({ ...prev, [name]: url || "" }));
  };

  /**
  * Updates form field when input changes.
  * @param {Event} e - Input event.
  * @param {string} name - Field name.
  * @param {string} data_type - Backend datatype.
  */
  function handleChange(e, name, data_type) {
    const raw = e && e.target ? e.target.value : e;
    const value = data_type === 'boolean' ? raw === 'true' : raw;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  /**
   * Validates all form fields based on metadata.
   * @returns {boolean} Whether all fields are valid.
   */
  function validate() {
    const errs = {};
    if (!fields) return true;

    fields.forEach((f) => {
      const val = formData[f.name];
      const type = f.data_type ? f.data_type.toLowerCase() : "";

      if (f.required && (val === "" || val === null || val === undefined)) {
        errs[f.name] = `${f.display_name} is required`;
        return;
      }

      if (
        val !== "" &&
        val !== null &&
        val !== undefined &&
        typeof val === "string" &&
        f.max_length &&
        type === "string" &&
        val.length > f.max_length
      ) {
        errs[f.name] = `${f.display_name} must be at most ${f.max_length} characters`;
        return;
      }

      // integer validation
      if (type === "integer" && val !== "" && val !== null) {
        if (!/^-?\d+$/.test(val.toString())) {
          errs[f.name] = `Please enter a valid whole number for ${f.display_name}.`;
          return;
        }

        const num = Number(val);
        const INT_MIN = -2147483648;
        const INT_MAX = 2147483647;
        if (num > INT_MAX) {
          errs[f.name] = `The number you entered for ${f.display_name} is too large. Please enter a smaller value.`;
          return;
        }
        if (num < INT_MIN) {
          errs[f.name] = `The number you entered for ${f.display_name} is too small. Please enter a larger value.`;
          return;
        }
      }

      // json validation — reject invalid JSON instead of silently coercing
      // it to null during payload building (which causes not-null constraint
      // violations on required fields and silent data loss on optional ones).
      if (
        type === "json" &&
        val !== "" &&
        val !== null &&
        val !== undefined &&
        typeof val === "string"
      ) {
        try {
          JSON.parse(val);
        } catch (e) {
          errs[f.name] = `${f.display_name} must be valid JSON — ${e.message}`;
          return;
        }
      }

      // geo_point validation — both coordinates must be supplied together and
      // in range. Otherwise the payload builder silently coerces the point to
      // null (not-null violations on required fields, silent data loss
      // otherwise). A required-but-empty point is an object, so it also slips
      // past the top-level required check above — handle that here too.
      if (type === "geo_point") {
        const geo = val && typeof val === "object" ? val : {};
        const latRaw = geo.lat ?? geo.latitude ?? "";
        const lngRaw = geo.lng ?? geo.longitude ?? "";
        const latFilled = latRaw !== "" && latRaw !== null && latRaw !== undefined;
        const lngFilled = lngRaw !== "" && lngRaw !== null && lngRaw !== undefined;

        if (!latFilled && !lngFilled) {
          if (f.required) {
            errs[f.name] = `${f.display_name} is required`;
            return;
          }
        } else if (!latFilled || !lngFilled) {
          errs[f.name] = `${f.display_name} requires both latitude and longitude`;
          return;
        } else {
          const lat = Number(latRaw);
          const lng = Number(lngRaw);
          if (isNaN(lat) || isNaN(lng)) {
            errs[f.name] = `${f.display_name} latitude and longitude must be valid numbers`;
            return;
          }
          if (lat < -90 || lat > 90) {
            errs[f.name] = `${f.display_name} latitude must be between -90 and 90`;
            return;
          }
          if (lng < -180 || lng > 180) {
            errs[f.name] = `${f.display_name} longitude must be between -180 and 180`;
            return;
          }
        }
      }

      // float validation
      if (type === "float" && val !== "" && val !== null) {
        if (isNaN(val)) {
          errs[f.name] = `Please enter a valid number for ${f.display_name}.`;
          return;
        }
      }

      // decimal validation
      if (type === "decimal" && val !== "" && val !== null) {
        if (isNaN(val)) {
          errs[f.name] = `Please enter a valid decimal number for ${f.display_name}.`;
          return;
        }

        const [integerPart, decimalPart] = val.toString().split(".");

        const totalDigits =
          (integerPart ? integerPart.replace("-", "").length : 0) +
          (decimalPart ? decimalPart.length : 0);

        if (f.numeric_precision && totalDigits > f.numeric_precision) {
          errs[f.name] = `${f.display_name} exceeds precision ${f.numeric_precision} (total digits allowed).`;
          return;
        }
        if (f.numeric_scale && decimalPart && decimalPart.length > f.numeric_scale) {
          errs[f.name] = `${f.display_name} exceeds scale ${f.numeric_scale} (digits after decimal).`;
          return;
        }
      }
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  /**
   * Handles cancel button → navigates back to table list.
   * @param {Event} e - Button click event.
   */
  async function handleCancel(e) {
    if (e) e.preventDefault();
    if (onCancel) {
      onCancel();
      return;
    }
    navigate(`/dashboard/tables/${tableName}?page=${fromPage}`);
  }

  /**
   * Loads record for update mode.
   * Priority:
   * 1) state record → from navigation
   * 2) initialData (rare)
   * 3) backend fetch using recordId
   * @async
   * @returns {Object|null} The fetched record or null.
   */
  async function loadFinalRecord() {
    if (recordState && (!recordId || recordState.id == recordId)) {
      return recordState;
    }

    if (initialData) {
      return initialData;
    }

    if (effectiveMode === "update" && recordId) {
      try {
        const fetchRecordPayload = {
          application_id: applicationId,
          organization_id: organizationId,
          table_name: tableName
        };

        const res = await apiHelper.apiCall(
          `/arcee/application/table/fetch/${recordId}/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fetchRecordPayload)
          }
        );

        const recJson = await res.json();

        if (!res.ok || !recJson || recJson.error) {
          setError(recJson?.error || "Record not found.");
          return null;
        }

        setFetchedRecord(recJson);
        return recJson;

      } catch (err) {
        setError(err.message || "Failed to fetch record details.");
        return null;
      }
    }

    return null;
  }

  /**
   * Handles form submission (create/update).
   * Builds payload → sends API request → shows success/error popup.
   * @async
   * @param {Event} e - Form submit event.
   */
  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    // ── Phase 1: upload any pending files to Flask BEFORE building the
    // record payload. If a customer abandons the form, nothing has been
    // sent yet — no orphan files end up in storage.
    //
    // We track every successful upload's URL so that if the record-save
    // step in Phase 3 fails, we can roll them back (delete from Flask)
    // and avoid leaving stale blobs behind.
    const uploadedThisSubmit = []; // [{ fieldName, url }, ...]
    const uploadedUrls = {};       // { fieldName: url }
    try {
      for (const [fieldName, file] of Object.entries(pendingUploads)) {
        const fieldDef = (fields || []).find(fd => fd.name === fieldName);
        const declaredType = fieldDef && fieldDef.data_type === 'image' ? 'image' : 'document';
        const { url } = await uploadPendingFile(file, {
          is_public: true,
          fileType: declaredType,
        });
        uploadedThisSubmit.push({ fieldName, url });
        uploadedUrls[fieldName] = url;
      }
    } catch (uploadErr) {
      // One of the file uploads failed. Roll back any earlier successful
      // ones so we don't leak partial files into Flask, then surface the
      // error and stop — the record will NOT be created/updated.
      for (const u of uploadedThisSubmit) {
        deleteUploadedFile(u.url);
      }
      setError(uploadErr.message || 'File upload failed. The record was not saved.');
      setLoading(false);
      return;
    }

    // ── Phase 2: build the record payload, splicing in any newly-uploaded URLs.
    const cleanedPayload = {};
    (fields || []).forEach((field) => {
      const key = field.name;
      if (key.endsWith('_display')) return;
      let value = (key in uploadedUrls) ? uploadedUrls[key] : formData[key];

      const nonStringTypes = [
        'integer',
        'float',
        'decimal',
        'date',
        'datetime',
        'boolean',
        'fk',
        'one-to-many',
        'one-to-one',
        'self',
      ];
      if ((value === '' || value === undefined) && (nonStringTypes.includes(field.data_type) || field.related_table_name)) {
        value = null;
      }

      if (field.data_type === 'datetime' && value && typeof value === 'string') {
        value = convertLocalStringToUTCISO(value);
      }

      if (field.data_type === 'uuid') return;

      if (field.data_type === 'json' && typeof value === 'string') {
        try { value = JSON.parse(value); } catch (e) { value = null; }
      }

      if (field.data_type === 'geo_point') {
        const lat = parseFloat(value?.lat);
        const lng = parseFloat(value?.lng);
        value = (!isNaN(lat) && !isNaN(lng)) ? { lat, lng } : null;
      }

      cleanedPayload[key] = value;
    });

    if (effectiveMode === "update" && recordId) {
      cleanedPayload["id"] = Number(recordId);
    }

    try {
      const payload = {
        application_id: applicationId,
        organization_id: organizationId,
        table_name: tableName,
        payload: cleanedPayload,
      };

      const apiUrl = '/arcee/application/table/save/record/';

      const res = await apiHelper.apiCall(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const resJson = await res.json();

      if (!res.ok) {
        // Record save failed — roll back the files we just uploaded in
        // Phase 1 so we don't leave them orphaned in Flask.
        for (const u of uploadedThisSubmit) {
          deleteUploadedFile(u.url);
        }
        // Pre-save hook vetoes return { error, blocked_by: 'record.before_*' }.
        // The veto reason is admin-authored business rule text (e.g. "Amount must be positive.")
        // and must be shown verbatim — the generic_error_message would defeat the whole point
        // of letting admins write specific, user-facing validation messages.
        const isHookVeto = typeof resJson.blocked_by === 'string'
          && resJson.blocked_by.startsWith('record.before_');
        let errorMsg;
        if (isHookVeto) {
          errorMsg = resJson.error || "This operation was blocked.";
        } else if (uiRules.generic_error_message && uiRules.generic_error_message.trim() !== "") {
          errorMsg = uiRules.generic_error_message;
        } else {
          errorMsg = resJson.error || "Failed to save record.";
        }
        setError(errorMsg);
        return;
      }

      if (!resJson.message) {
        // Same rollback as the !res.ok branch
        for (const u of uploadedThisSubmit) {
          deleteUploadedFile(u.url);
        }
        setError("Unexpected server response.");
        return;
      }

      // ── Phase 3: record persisted successfully. Now clean up any
      // previously-stored files that were replaced or cleared in this
      // edit. Best-effort — failures here don't break the save (the
      // record is already good).
      const oldUrlsToRemove = [];
      Object.entries(originalFileUrls).forEach(([fieldName, oldUrl]) => {
        const newUrl = cleanedPayload[fieldName];
        if (oldUrl && oldUrl !== newUrl) {
          oldUrlsToRemove.push(oldUrl);
        }
      });
      for (const oldUrl of oldUrlsToRemove) {
        deleteUploadedFile(oldUrl);
      }
      // Reset the lift-up state so a follow-up edit starts clean.
      setPendingUploads({});

      if (resJson.message) {
        const successMsg = effectiveMode === 'create'
          ? (uiRules.insert_success_message || 'Record saved successfully!')
          : (uiRules.update_success_message || 'Record saved successfully!');

        const successRule = effectiveMode === 'create'
          ? uiRules.on_insert_success
          : uiRules.on_update_success;

        const action = successRule?.action || 'show';
        const identifier = successRule?.identifier || 'list';

        showAlertDialog({
          title: 'Success',
          message: successMsg,
          type: 'success',
          showCancel: false,
          okLabel: 'OK',
          onOk: () => {
            queryClient.invalidateQueries({
              queryKey: ["tableData", tableName],
            });

            if (action === 'redirect' && identifier === 'record') {
              const rawId =
                resJson.id ??
                resJson.data?.id ??
                (Array.isArray(resJson.ids) ? resJson.ids[0] : undefined) ??
                (Array.isArray(resJson.data?.ids) ? resJson.data.ids[0] : undefined);

              const newId = rawId ?? recordId;
              if (newId) {
                navigate(`/dashboard/tables/${tableName}/${newId}`);
              } else {
                navigate(`/dashboard/tables/${tableName}?page=${fromPage}`);
              }
            } else if (action === 'redirect' && identifier === 'form') {
              navigate(`/dashboard/tables/${tableName}/add`);
              if (effectiveMode === 'create') {
                window.location.reload();
              }
            } else {
              navigate(`/dashboard/tables/${tableName}?page=${fromPage}`);
            }
          },
        });
      }
    } catch (err) {
      setError(err.message || "Failed to save record.");
    } finally {
      setLoading(false);
    }
  }

  const uniformStyle = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: `1.5px solid ${theme.primaryLight}`,
    borderRadius: theme.buttonRadius,
    marginTop: '0.25rem',
    fontSize: '1rem',
    boxSizing: 'border-box',
  };

  const focusedStyle = {
    borderColor: '#86b7fe',
    boxShadow: '0 0 0 0.15rem rgba(13,110,253,0.25)',
    outline: 'none',
  };

  /**
   * Builds the info-line displayed under every field label.
   * @param {Object} f - Field metadata.
   * @returns {JSX.Element} Info block.
   */
  const renderFieldInfo = (f) => {

    // 1) Try role-based hint from ui_rules
    const roleHint = getFieldHintForRole(f, currentRoleName);

    if (roleHint) {
      return (
        <div style={{ fontSize: 13, color: "#636979", marginTop: 4 }}>
          <i className="bi bi-info-circle" /> <span>{roleHint}</span>
        </div>
      );
    }

    const requiredText = f.required ? 'This field is required' : 'This field is optional';
    const typeText = `and is of type ${readableType(f.data_type)}.`;
    const isUnique = f.is_unique ? ', does not allow duplicate values' : '';
    let extraText = "";
    if (f.data_type === "integer") {
      extraText = " This value must be between -2147483648 and 2147483647.";
    }
    if (f.data_type === "decimal" && f.numeric_precision) {
      extraText = `Precision: ${f.numeric_precision}, Scale: ${f.numeric_scale}.Total digits allowed = ${f.numeric_precision}, with ${f.numeric_scale} digits after decimal.`;
    }
    if (f.data_type === "float") {
      extraText = " Floating values support decimal and scientific notation.";
    }
    if (f.data_type === "date") {
      extraText = " Format: YYYY-MM-DD.";
    }
    if (f.data_type === "datetime") {
      extraText = " Stored in UTC format.";
    }
    if (f.data_type === "boolean") {
      extraText = " Choose either True or False.";
    }
    if (f.data_type === "boolean") {
      extraText = " Choose either True or False.";
    }
    if (["fk", "one-to-one", "one-to-many", "self"].includes(f.data_type)) {
      if (f.data_type === "fk") {
        extraText = ` This is a Foreign Key linking to the "${f.related_table_name}" table. Select a record from that table.`;
      }
      if (f.data_type === "one-to-one") {
        extraText = ` This creates a One-to-One relationship with the "${f.related_table_name}" table. Each record links to exactly one record in that table.`;
      }
      if (f.data_type === "one-to-many") {
        extraText = ` This defines a One-to-Many relationship with the "${f.related_table_name}" table. One record here can be linked to multiple records in that table.`;
      }
      if (f.data_type === "self") {
        extraText = ` This is a Self-Reference, meaning this field links to another record within the same table.`;
      }
    }
    const maxLenText = f.max_length ? ` Max length of chars allowed: ${f.max_length}.` : '';
    const relationNote =
      f.related_table_name ? ' For relationship type fields, the selected record ID is stored.' : '';

    return (
      <div style={{ fontSize: 13, color: '#636979', marginTop: 2 }}>
        <i className="bi bi-info-circle" />{' '}
        <b >{requiredText}{isUnique} {typeText}</b>
        {maxLenText}
        {extraText}
        {relationNote}
      </div>
    );
  };

  /**
   * Renders the appropriate input component based on field datatype.
   * @param {Object} f - Field metadata.
   * @returns {JSX.Element} Input field.
   */
  function renderField(f) {
    const isReadOnly = f.__readonly === true;
    const isFocused = focusedField === f.name;
    const style = isFocused ? { ...uniformStyle, ...focusedStyle } : uniformStyle;

    const placeholder = getFieldPlaceholder(f);

    const common = {
      id: f.name,
      name: f.name,
      value: formData[f.name],
      required: f.required,
      placeholder: placeholder,
      onChange: isReadOnly ? undefined : (e) => handleChange(e, f.name, f.data_type),
      onFocus: () => setFocusedField(f.name),
      onBlur: () => setFocusedField(null),
      style,
      readOnly: isReadOnly,
      disabled: isReadOnly
    };

    if (f.related_table_name) {
      const displayValue = formData[`${f.name}_display`] || '';
      return (
        <div style={{ position: 'relative' }}>
          <input
            className="dynamic-form-input"
            {...common}
            value={displayValue}
            readOnly
            onClick={() => openDialog(f.name, f.related_table_name)}
            placeholder={`Select ${f.display_name}`}
            style={{ ...style, cursor: 'pointer', paddingRight: '2rem' }}
          />
          {displayValue && (
            <span
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  [f.name]: null,
                  [`${f.name}_display`]: '',
                }))
              }
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: 'red',
                fontWeight: 'bold',
                fontSize: '18px',
                lineHeight: 1,
              }}
              title="Clear selection"
            >
              ×
            </span>
          )}
        </div>
      );
    }

    if (f.data_type === 'text') {
      const options = getFieldOptionsForRole(f, currentRoleName);

      if (options.length > 0) {
        return (
          <select
            className="form-select enum-select"
            id={f.name}
            name={f.name}
            value={formData[f.name] || ""}
            onChange={(e) => handleChange(e, f.name, "string")}
            required={f.required}
            disabled={isReadOnly}
            style={{
              width: "100%",
              fontSize: "1rem",
              padding: "0.55rem 0.75rem",
              borderRadius: theme.buttonRadius,
              border: `1.5px solid ${theme.primaryLight}`,
            }}
          >
            <option
              className="enum-placeholder"
              value="">
              {placeholder || `Select ${f.display_name}`}
            </option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      return <textarea rows={3} className="dynamic-form-textarea" {...common} style={{ ...style, resize: 'vertical' }} />;
    }

    if (f.data_type === 'integer') return <input type="text" className="dynamic-form-textarea" {...common} />;

    if (['string', 'decimal', 'float'].includes((f.data_type || '').toLowerCase())) {
      const options = getFieldOptionsForRole(f, currentRoleName);
      if (options.length > 0 && f.data_type === 'string') {
        return (
          <select
            className="form-select enum-select"
            id={f.name}
            name={f.name}
            value={formData[f.name] || ""}
            onChange={(e) => handleChange(e, f.name, f.data_type)}
            required={f.required}
            disabled={isReadOnly}
            style={{
              width: "100%",
              fontSize: "1rem",
              padding: "0.55rem 0.75rem",
              borderRadius: theme.buttonRadius,
              border: `1.5px solid ${theme.primaryLight}`,
            }}
          >
            <option
              className="enum-placeholder"
              value="">
              {placeholder || `Select ${f.display_name}`}
            </option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      return <input className="dynamic-form-input" type="text" {...common} maxLength={f.max_length || undefined} />;
    }

    if (f.data_type === "enum") {

      const uiOptions = getFieldOptionsForRole(f, currentRoleName);
      const hasUiOptions = uiOptions.length > 0;

      const options = hasUiOptions
        ? uiOptions
        : (f.allowed_options
          ? f.allowed_options.split(",").map((o) => ({ label: o.trim(), value: o.trim() }))
          : []);

      /** Previous Logic we just used to read from allowed_options
      const options = f.allowed_options
        ? f.allowed_options.split(",").map(o => o.trim())
        : [];
        */
      return (
        <select
          className="form-select enum-select"
          id={f.name}
          name={f.name}
          value={formData[f.name] || ""}
          onChange={(e) => handleChange(e, f.name, "enum")}
          required={f.required}
          style={{
            width: "100%",
            fontSize: "1rem",
            padding: "0.55rem 0.75rem",
            borderRadius: theme.buttonRadius,
            border: `1.5px solid ${theme.primaryLight}`,
          }}
        >
          <option className="enum-placeholder" value="">
            Select {f.display_name}
          </option>

          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (f.data_type === 'boolean')
      return (
        <div style={{ marginTop: '0.25rem' }}>
          <label style={{ marginRight: 20 }}>
            <input
              type="radio"
              name={f.name}
              value="true"
              checked={formData[f.name] === true}
              onChange={(e) => handleChange(e, f.name, 'boolean')}
            />{' '}
            True
          </label>
          <label>
            <input
              className="dynamic-form-input"
              type="radio"
              name={f.name}
              value="false"
              checked={formData[f.name] === false}
              onChange={(e) => handleChange(e, f.name, 'boolean')}
            />{' '}
            False
          </label>
        </div>
      );

    if (f.data_type === 'date')
      return (
        <CalendarInput
          selected={formData[f.name] ? new Date(formData[f.name]) : null}
          onChange={(date) =>
            handleChange({ target: { value: date ? date.toISOString().split('T')[0] : '' } }, f.name, f.data_type)
          }
          placeholder={placeholder}
        />
      );

    if (f.data_type === 'datetime')
      return (
        <CalendarInput
          selected={formData[f.name] ? new Date(formData[f.name]) : null}
          onChange={(date) => handleChange({ target: { value: date ? date.toISOString() : '' } }, f.name, f.data_type)}
          placeholder={placeholder}
          showTimeSelect
        />
      );

    if (f.data_type === 'image' || f.data_type === 'file') {
      // Determine allowed types based on exact field type
      const allowedFileTypes = f.data_type === 'image'
        ? ["image"]
        : ["document", "archive"]; // 'file' allows broad types by default, or restricted subset if preferred

      return (
        <FileUploadInput
          is_public={true}
          value={formData[f.name] || ""}
          onChange={(url) => handleImageChange(url, f.name)}
          pendingFile={pendingUploads[f.name] || null}
          onPendingFile={(file, fieldName) => {
            // Lift the picked File (or null on clear) into the form's state.
            // The actual Flask upload happens inside handleSubmit.
            setPendingUploads((prev) => {
              const next = { ...prev };
              if (file) next[fieldName] = file;
              else delete next[fieldName];
              return next;
            });
          }}
          allowedFileTypes={allowedFileTypes}
          inputProps={{
            name: f.name
          }}
          placeholder={placeholder}
        />
      );
    }

    if (f.data_type === 'json') {
      const jsonStr = formData[f.name] != null && typeof formData[f.name] === 'object'
        ? JSON.stringify(formData[f.name], null, 2)
        : (formData[f.name] || '');
      let jsonError = null;
      if (jsonStr) {
        try { JSON.parse(jsonStr); } catch (e) { jsonError = "Invalid JSON — " + e.message; }
      }
      return (
        <div>
          <textarea
            className="dynamic-form-textarea"
            id={f.name}
            name={f.name}
            rows={5}
            value={jsonStr}
            readOnly={isReadOnly}
            disabled={isReadOnly}
            placeholder='{"key": "value"}'
            onChange={e => {
              setFormData(prev => ({ ...prev, [f.name]: e.target.value }));
            }}
            style={{ ...style, fontFamily: "monospace", fontSize: "0.9rem", resize: "vertical" }}
          />
          {jsonError && <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>{jsonError}</div>}
        </div>
      );
    }

    if (f.data_type === 'geo_point') {
      const geo = formData[f.name] || {};
      const lat = geo.lat ?? geo.latitude ?? '';
      const lng = geo.lng ?? geo.longitude ?? '';
      const latErr = lat !== '' && (Number(lat) < -90  || Number(lat) > 90)  ? "Latitude must be between -90 and 90."   : null;
      const lngErr = lng !== '' && (Number(lng) < -180 || Number(lng) > 180) ? "Longitude must be between -180 and 180." : null;
      function updateGeo(field, val) {
        setFormData(prev => ({ ...prev, [f.name]: { ...(prev[f.name] || {}), [field]: val } }));
      }
      return (
        <div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 12, color: "#636979" }}>Latitude</label>
              <input
                type="number"
                className="dynamic-form-input"
                value={lat}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                placeholder="-90 to 90"
                step="any"
                onChange={e => updateGeo("lat", e.target.value)}
                style={style}
              />
              {latErr && <div style={{ color: "red", fontSize: 12, marginTop: 2 }}>{latErr}</div>}
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 12, color: "#636979" }}>Longitude</label>
              <input
                type="number"
                className="dynamic-form-input"
                value={lng}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                placeholder="-180 to 180"
                step="any"
                onChange={e => updateGeo("lng", e.target.value)}
                style={style}
              />
              {lngErr && <div style={{ color: "red", fontSize: 12, marginTop: 2 }}>{lngErr}</div>}
            </div>
          </div>
        </div>
      );
    }

    if (f.data_type === 'uuid') {
      if (effectiveMode === 'create') return null;
      return (
        <div>
          <input
            type="text"
            className="dynamic-form-input"
            value={formData[f.name] || ''}
            readOnly
            disabled
            style={{ ...style, fontFamily: "monospace", fontSize: "0.9rem", color: "#666" }}
          />
          <div style={{ fontSize: 12, color: "#636979", marginTop: 2 }}>
            <i className="bi bi-info-circle" /> Auto-generated — cannot be changed.
          </div>
        </div>
      );
    }

    return <input className="dynamic-form-input" type="text" {...common} />;
  }

  if (loading && !fields) return <ProgressBarDialog open text_message="Loading Form..." />;

  if (!fields) return null;

  return (
    <div style={{ maxWidth: 1000, margin: '2rem auto', padding: '0 1rem', width: '90%' }}>
      <GradientBar height={10} />
      <ProgressBarDialog open={loading} text_message={effectiveMode === 'create' ? 'Creating a table record...' : 'Saving changes...'} />
      <AlertDialog
        open={!!error}
        title="Error"
        message={error}
        type="error"
        okLabel="Close"
        showCancel={false}
        onOk={() => {
          setError(null);
        }}
      />
      <form
        noValidate
        onSubmit={handleSubmit}
        style={{
          background: '#fff',
          borderRadius: theme.cardRadius,
          boxShadow: '0 4px 24px rgba(65,70,150,0.13)',
          padding: '32px 26px 22px 26px',
        }}
      >
        <h3 style={{ color: theme.primary, fontWeight: 700, marginBottom: 22 }}>
          {effectiveMode === 'create'
            ? (uiRules.insert_form_title || `Create Record of ${tableDisplayName}`)
            : (uiRules.update_form_title || `Edit Record of ${tableDisplayName}`)}
        </h3>

        {fields.map((f) => {

          const currentUserRoles = currentRoleName ? [currentRoleName] : [];
          if (!isFieldVisible(f, formData, currentUserRoles)) {
            return null; // do not render this field
          }
          const readOnly = isFieldReadOnly(f, formData, currentUserRoles);
          const immutable = effectiveMode === 'update' && f.editable_after_create === false;

          return (
            <div key={f.name} style={{ marginBottom: '1.5rem' }}>
              <label htmlFor={f.name} style={{ color: theme.primary, fontWeight: 600, fontSize: '1.1rem' }}>
                {f.display_name} {f.required && <span style={{ color: 'red' }}>*</span>}
                {immutable && (
                  <i className="bi bi-lock-fill ms-2" style={{ fontSize: 13, color: "#888" }}
                    title="Cannot be changed after creation" />
                )}
              </label>

              {renderField({ ...f, __readonly: readOnly || immutable })}
              {immutable && (
                <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                  <i className="bi bi-info-circle" /> Cannot be changed after creation.
                </div>
              )}
              {!immutable && renderFieldInfo(f)}

              {errors[f.name] && <div style={{ color: 'red', marginTop: 6 }}>{errors[f.name]}</div>}
            </div>
          );
        })}

        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: 30, flexWrap: 'wrap' }}>
          <Button mode="outlined" type="button" onClick={handleCancel} disabled={loading}>
            {loading ? 'Cancelling..' : 'Cancel'}
          </Button>

          <Button mode="primary-bold" type="submit" disabled={loading}>
            {loading ? 'Saving...' : (effectiveMode === 'create'
              ? (uiRules.insert_button_label || 'Create')
              : (uiRules.update_button_label || 'Update'))}
          </Button>
        </div>

        {dialogOpen && (
          <DynamicTableRecordShowDialog
            open={dialogOpen}
            tableName={dialogTableName}
            mode="select"
            onSelect={handleDialogSelect}
            onClose={() => setDialogOpen(false)}
          />
        )}
      </form>
    </div>
  );
}
